import { Add, ArrowBack } from "@mui/icons-material";
import {
    AppBar,
    Box,
    Button,
    Container,
    IconButton,
    Tab,
    Tabs,
    Toolbar,
    Typography,
} from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import React, { useContext, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AppContext } from "../../AppContext";
import HelmetMeta from "../../components/HelmetMeta";
import { useLocalization } from "../../i18n/LocalizationProvider";
import docTypeFields from "../../models/DocTypeFields";
import { useGetDocTypeList } from "../../shared/hooks/useDocTypesData";
import AddonsTab from "./components/AddonsTab";
import ItemDialog from "./components/ItemDialog";
import ItemGroupDialog from "./components/ItemGroupDialog";
import ItemGroupsTab from "./components/ItemGroupsTab";
import MenuItemsTab from "./components/MenuItemsTab";
import PurchaseItemsTab from "./components/PurchaseItemsTab";

function TabPanel({ children, value, index, ...other }) {
    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`simple-tabpanel-${index}`}
            aria-labelledby={`simple-tab-${index}`}
            {...other}
        >
            {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
        </div>
    );
}

const Items = () => {
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const { dir } = useLocalization();
    const { refetchMenuItems: refetchAppMenuItems } = useContext(AppContext);
    const [tabValue, setTabValue] = useState(0);
    const [itemDialogOpen, setItemDialogOpen] = useState(false);
    const [itemGroupDialogOpen, setItemGroupDialogOpen] = useState(false);
    const [selectedItem, setSelectedItem] = useState(null);
    const [selectedItemGroup, setSelectedItemGroup] = useState(null);
    const [itemGroupDefaultData, setItemGroupDefaultData] = useState(null);
    const [refreshTrigger, setRefreshTrigger] = useState(0);

    // Fetch data
    const { data: itemGroups, refetch: refetchItemGroups } = useGetDocTypeList({
        doctype: "Item Group",
        fields: docTypeFields.ItemGroup,
    });

    const { data: menuItems, refetch: refetchMenuItems } = useGetDocTypeList({
        doctype: "Item",
        fields: docTypeFields.Item,
        filters: [["item_type", "=", "Menu"]],
    });

    const { data: addonItems, refetch: refetchAddonItems } = useGetDocTypeList({
        doctype: "Item",
        fields: docTypeFields.Item,
        filters: [["item_type", "=", "Addons"]],
    });

    const { data: purchaseItems, refetch: refetchPurchaseItems } =
        useGetDocTypeList({
            doctype: "Item",
            fields: docTypeFields.Item,
            filters: [["is_purchase_item", "=", 1]],
        });

    const handleTabChange = (event, newValue) => {
        setTabValue(newValue);
    };

    const handleAddNew = () => {
        if (tabValue === 0) {
            // Item Groups tab
            setSelectedItemGroup(null);
            setItemGroupDialogOpen(true);
        } else {
            // Items tabs
            setSelectedItem(null);
            setItemDialogOpen(true);
        }
    };

    const handleEditItem = (item) => {
        setSelectedItem(item);
        setItemDialogOpen(true);
    };

    const handleEditItemGroup = (itemGroup, defaultData = null) => {
        setSelectedItemGroup(itemGroup);
        setItemGroupDefaultData(defaultData);
        setItemGroupDialogOpen(true);
    };

    const handleDialogClose = () => {
        setItemDialogOpen(false);
        setItemGroupDialogOpen(false);
        setSelectedItem(null);
        setSelectedItemGroup(null);
        setItemGroupDefaultData(null);
        // Note: Individual dialogs now handle their own refetching
        // This ensures data is refreshed at the right time
    };

    const handleItemRefetch = () => {
        // Refetch AppContext menu items for global state
        refetchAppMenuItems();

        // Refetch local items based on current tab
        if (tabValue === 1) {
            refetchMenuItems();
        } else if (tabValue === 2) {
            refetchAddonItems();
        } else if (tabValue === 3) {
            refetchPurchaseItems();
        }
        setRefreshTrigger((prev) => prev + 1);
    };

    const handleItemGroupRefetch = () => {
        // Invalidate item group queries
        queryClient.invalidateQueries(["getItemGroups"]);
        queryClient.invalidateQueries(["getGroupSubGroups"]);

        // Refetch local item groups data
        refetchItemGroups();
        setRefreshTrigger((prev) => prev + 1);
    };

    const getItemType = () => {
        switch (tabValue) {
            case 1:
                return "Menu";
            case 2:
                return "Addons";
            case 3:
                return "Purchase";
            default:
                return "Menu";
        }
    };

    return (
        <React.Fragment>
            <HelmetMeta title="Items Management" />
            <Box sx={{ flexGrow: 1, direction: dir }}>
                <AppBar position="static" color="primary">
                    <Toolbar
                        sx={{
                            flexDirection:
                                dir === "rtl" ? "row-reverse" : "row",
                        }}
                    >
                        <IconButton
                            edge="start"
                            color="inherit"
                            onClick={() => navigate("/tables")}
                            sx={{
                                mr: dir === "rtl" ? 0 : 2,
                                ml: dir === "rtl" ? 2 : 0,
                            }}
                        >
                            <ArrowBack />
                        </IconButton>
                        <Typography
                            variant="h6"
                            component="div"
                            sx={{
                                flexGrow: 1,
                                textAlign: dir === "rtl" ? "right" : "left",
                            }}
                        >
                            Items Management
                        </Typography>
                        <Button
                            color="inherit"
                            startIcon={<Add />}
                            onClick={handleAddNew}
                            variant="outlined"
                        >
                            Add New
                        </Button>
                    </Toolbar>
                </AppBar>

                <Container maxWidth="xl" sx={{ mt: 2 }}>
                    <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
                        <Tabs value={tabValue} onChange={handleTabChange}>
                            <Tab label="Item Groups" />
                            <Tab label="Menu Items" />
                            <Tab label="Addons" />
                            <Tab label="Purchase Items" />
                        </Tabs>
                    </Box>

                    <TabPanel value={tabValue} index={0}>
                        <ItemGroupsTab
                            itemGroups={itemGroups || []}
                            onEdit={handleEditItemGroup}
                            refreshTrigger={refreshTrigger}
                            refetchItemGroups={refetchItemGroups}
                        />
                    </TabPanel>

                    <TabPanel value={tabValue} index={1}>
                        <MenuItemsTab
                            items={menuItems || []}
                            itemGroups={itemGroups || []}
                            onEdit={handleEditItem}
                            refreshTrigger={refreshTrigger}
                            refetchItems={refetchMenuItems}
                        />
                    </TabPanel>

                    <TabPanel value={tabValue} index={2}>
                        <AddonsTab
                            items={addonItems || []}
                            itemGroups={itemGroups || []}
                            onEdit={handleEditItem}
                            refreshTrigger={refreshTrigger}
                            refetchItems={refetchAddonItems}
                        />
                    </TabPanel>

                    <TabPanel value={tabValue} index={3}>
                        <PurchaseItemsTab
                            items={purchaseItems || []}
                            itemGroups={itemGroups || []}
                            onEdit={handleEditItem}
                            refreshTrigger={refreshTrigger}
                            refetchItems={refetchPurchaseItems}
                        />
                    </TabPanel>
                </Container>
            </Box>

            <ItemDialog
                open={itemDialogOpen}
                onClose={handleDialogClose}
                item={selectedItem}
                itemType={getItemType()}
                itemGroups={itemGroups || []}
                addonItems={addonItems || []}
                onRefetch={handleItemRefetch}
            />

            <ItemGroupDialog
                open={itemGroupDialogOpen}
                onClose={handleDialogClose}
                itemGroup={selectedItemGroup}
                itemGroups={itemGroups || []}
                defaultData={itemGroupDefaultData}
                onRefetch={handleItemGroupRefetch}
            />
        </React.Fragment>
    );
};

export default Items;
